import Button from "@/components/button/Button"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { SvgIcon } from "@/components/icon"
import { globalFetchPolicy } from "@/components/layout/fetchPolicies"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { useOrganizationsGeneralInfoQuery } from "@/graphql"
import { useBreakpoint } from "@/hooks/useBreakpoint"
import { Row, Skeleton, Tooltip } from "antd"

import { FC } from "react"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"
import "./OrgLanding.less"

import heroVideo from "./asset/hero-video.mp4"
import logoName from "./asset/logo-name.png"

const { Title, Paragraph } = WebsiteTypography

type Props = {
  loading?: boolean
  content: {
    title: string
    subtitle: string
    bgImage?: string
  }
}

const OrgLanding: FC<Props> = ({ loading, content }) => {
  const params = useParams()
  const navigate = useNavigate()
  const currentOrgSlug = params.org

  const { openDarkModal } = useModalDarkContext()

  const { xxl, xl, lg, md, sm } = useBreakpoint()

  const { data, loading: orgLoading } = useOrganizationsGeneralInfoQuery({
    fetchPolicy: globalFetchPolicy,
    variables: {
      filter: { slug: { eqi: currentOrgSlug } },
    },
  })
  const currentOrganization = data?.organizations?.data?.[0]
  const currentOrganizationTitle = currentOrganization?.attributes?.name

  const userGuideUrl = currentOrganization?.attributes?.userGuide?.data?.attributes?.url
  const heroLogo = currentOrganization?.attributes?.heroLogo?.data?.attributes?.url

  const onSignatureClick = () => {
    navigate("/")
  }

  return (
    <>
      <div className={"org-landing-wrapper"}>
        <div className={"container"}>
          <div className={"org-landing-content-wrapper"}>
            <div className={"org-landing-content"}>
              {loading || orgLoading ? (
                <div className={"title-wrapper"}>
                  <Skeleton paragraph={{ rows: 8 }} active />
                </div>
              ) : (
                <div className={"org-hero-title-wrapper"}>
                  <Title level={1} sizeAs={2} color={"white"} className={"hero-title"}>
                    {currentOrganizationTitle && (
                      <>
                        <span className={"bold"}>{currentOrganizationTitle}</span>{" "}
                      </>
                    )}
                    <span className={"w300"}>{content.title}</span>
                  </Title>

                  <Paragraph
                    size={xxl || xl || lg || md || sm ? "lg" : "sm"}
                    color={"white"}
                    weight={"w600"}
                    className={"hero-text"}
                    style={{ padding: "30px 0" }}
                  >
                    {content.subtitle}
                  </Paragraph>

                  <Row justify={"center"}>
                    <Tooltip placement={"bottom"} title={!userGuideUrl ? "User guide is coming soon" : ""}>
                      <Button
                        btnType={"primary"}
                        size={"large"}
                        text={"SEE USER GUIDE"}
                        icon={<SvgIcon type={"pdf-logo"} />}
                        onClick={() => {
                          if (userGuideUrl) window.open(userGuideUrl, "_blank", "noopener,noreferrer")
                        }}
                      />
                    </Tooltip>
                  </Row>
                </div>
              )}
            </div>

            {heroLogo && (
              <div className={"hero-logo-img-wrapper"}>
                <img src={heroLogo} alt={""} />
              </div>
            )}
          </div>
        </div>

        <div className={"org-landing-signature-group"}>
          <button className={"org-landing-signature"} onClick={onSignatureClick}>
            <Paragraph size={xxl || xl || lg || md ? "xl" : "lg"} style={{ whiteSpace: "nowrap", paddingRight: 8 }}>
              {"Powered by "}
            </Paragraph>

            <div className={"org-landing-signature-img-wrapper"}>
              <img src={logoName} alt={"easetech logo"} />
            </div>
          </button>
        </div>

        <div className={"org-landing-bg-image-wrapper"}>
          {loading ? (
            <Skeleton />
          ) : (
            <video src={heroVideo} poster={content?.bgImage} autoPlay muted loop playsInline aria-hidden={"true"} />
          )}
        </div>
      </div>
    </>
  )
}

export default OrgLanding
